import styled from "@emotion/styled";
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatNumber, i18n, persistor, ProductTypeCodeEnum, setProgressBarPercentage, useAppDispatch, usePaymentSlipAdditional } from 'src/common';
import { processQueryResult } from 'src/common/api/apollo';
import { useGetGeneralTransferPaymentDetailsQuery } from "src/common/api/apollo/additional";
import { BarCode, Button, Card, Flex, Loader, Text } from 'src/components';
import { colors } from 'src/theme';


const CardContainerUp = styled(Flex)({
  padding: '8px 16px',
  backgroundColor: colors.primary1,
  borderRadius: '12px 12px 0 0'
})

const CardContainerDown = styled(Flex)({
  padding: '8px 16px',
  borderRadius: '12px'
})

const FlexCard = styled(Flex)({
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: colors.background6,
  width: '100%'
})

const FlexStyle = styled(Flex)({
  width: '100%'
})

const CheckoutPaymentSentWithInstallmentsPageAdditional = () => {
  const dispatch = useAppDispatch();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const { loading: loadingPaymentOptions, data: dataPaymentOptions } = useGetGeneralTransferPaymentDetailsQuery({
    // Treba poslati 12 i 1
    variables: { id: offerId, input: { installments: 12, type: ProductTypeCodeEnum.DODATNO } },
    fetchPolicy: 'cache-first',
    onCompleted: (result: any) => {
      processQueryResult(result);
    },
  });


  useEffect(() => {
    dispatch(setProgressBarPercentage(100));
    persistor.purge();
  }, []);

  const { downloadSlip, loading, PaymentSlip } = usePaymentSlipAdditional(offerId, 'monthly');

  if (loading || loadingPaymentOptions || !dataPaymentOptions) return <Loader page />;

  const gtpd = dataPaymentOptions.getGeneralTransferPaymentDetails;
  const price = gtpd?.price;
  const monthlyPrice = price != null ? price / 12 : null;

  // Definirajte podatke za prikaz u array-u
  const paymentDetails = [
    {
      label: i18n.t('pages.checkoutPaymentSentPage.payer'),
      value: `${dataPaymentOptions.getGeneralTransferPaymentDetails?.customerFirstName} ${dataPaymentOptions.getGeneralTransferPaymentDetails?.customerLastName}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.receiver'),
      value: (
        <Flex vertical align='flex-end'>
          <Text type="small" color={colors.text1}>{dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientName}</Text>
          <Text type="small" color={colors.text1}>{dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAddress}</Text>
          <Text type="small" color={colors.text1}>{`${dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientZipCode} ${dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientCity}`}</Text>
        </Flex>
      ),
      align: 'flex-start' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.amount'),
      value: monthlyPrice != null ? `${formatNumber(monthlyPrice)}€` : '',
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.referenceNumber'),
      value: `${dataPaymentOptions.getGeneralTransferPaymentDetails?.referencePrefix}${dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceNumberType === 'oib' ? dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceNumber : ''}${dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceSuffix}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.model'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.model,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.description'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.description,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.iban'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAccountNumber,
      align: 'center' as const
    }
  ];

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <Text type="xxxLarge" bold>{i18n.t('pages.checkoutPaymentSentPage.payment')}</Text>
        <Text type="base">{i18n.t('pages.checkoutPaymentSentPage.paymentSubtitle')}</Text>
      </Flex>
      <Card padding='0px' radius={12}>
        <CardContainerUp justify='space-between'>
          <Text type="small" color={colors.white}>{i18n.t('pages.checkoutPaymentSentPage.toPay')}</Text>
          <Text type="small" color={colors.white} bold>{monthlyPrice != null ? `${formatNumber(monthlyPrice)}€` : ''}</Text>
        </CardContainerUp>
        <CardContainerDown vertical gap={24}>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.barcodePayment')}</Text>
            {price != null && (
              <BarCode
                price={formatNumber(price / 12)}
                userFirstName={gtpd?.customerFirstName || ''}
                userLastName={gtpd?.customerLastName || ''}
                userAddress={gtpd?.customerAddress || ''}
                userCity={gtpd?.customerCity || ''}
                oib={gtpd?.customerOib || ''}
                recipient={gtpd?.recipientName || ''}
                recipientAddress={gtpd?.recipientAddress || ''}
                recipientCity={gtpd?.recipientCity || ''}
                model={gtpd?.model || ''}
                iban={gtpd?.recipientAccountNumber || ''}
                description={gtpd?.description || ''}
                hidden={false}
              />
            )}
          </Flex>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.or')}</Text>
          </Flex>
          <Flex vertical gap={8} align='center'>
            <PaymentSlip />
            <Button btnType='textBlack' size='small' onClick={() => downloadSlip()}>{i18n.t('pages.checkoutPaymentSentPage.downloadSlip')}</Button>
            <FlexStyle vertical gap={2} align='center'>
              {paymentDetails.map((detail, index) => (
                <FlexCard key={index} gap={16} align={detail.align} justify='space-between'>
                  <Text type="tiny" color={colors.text4}>{detail.label}</Text>
                  {typeof detail.value === 'string' ? (
                    <Text type="small" color={colors.text1}>{detail.value}</Text>
                  ) : (
                    detail.value
                  )}
                </FlexCard>
              ))}
            </FlexStyle>
          </Flex>

        </CardContainerDown>
      </Card>
    </Flex>

  );
}

export default CheckoutPaymentSentWithInstallmentsPageAdditional;
